import React, { useState } from 'react';
import './FAQAccordion.css';

/**
 * FAQAccordion — Reusable expand/collapse FAQ list.
 *
 * Props:
 *   items: Array<{
 *     question: string,
 *     summary?: string,    // optional one-sentence TL;DR shown above the divider
 *     answer: string | ReactNode  // full answer, supports \n\n paragraph breaks
 *                                 // and **bold** markdown in strings
 *   }>
 *   variant: 'marketing' | 'page'  (controls visual density)
 *   defaultOpenIndex: number | null (which item starts expanded)
 */

// Render a string with **bold** markdown as React fragments.
function renderMarkdown(text) {
  if (typeof text !== 'string') return text;
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}
export default function FAQAccordion({ items, variant = 'page', defaultOpenIndex = null }) {
  const [openIndex, setOpenIndex] = useState(defaultOpenIndex);

  const toggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className={`faq-accordion faq-accordion-${variant}`}>
      {items.map((item, index) => {
        const isOpen = openIndex === index;
        return (
          <div
            key={index}
            className={`faq-item ${isOpen ? 'open' : ''}`}
          >
            <button
              type="button"
              className="faq-question"
              onClick={() => toggle(index)}
              aria-expanded={isOpen}
            >
              <span className="faq-question-text">{item.question}</span>
              <span className="faq-chevron" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </span>
            </button>
            {isOpen && (
              <div className="faq-answer">
                {item.summary && (
                  <>
                    <div className="faq-summary">
                      <span className="faq-summary-label">Quick answer:</span>{' '}
                      {renderMarkdown(item.summary)}
                    </div>
                    <div className="faq-summary-divider" />
                  </>
                )}
                {typeof item.answer === 'string'
                  ? item.answer.split('\n\n').map((para, i) => (
                      <p key={i}>{renderMarkdown(para)}</p>
                    ))
                  : item.answer}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
